import { Stack, Typography } from '@mui/material'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { WorkflowStateAction } from '@/types/workflow-state-actions'
import AddDocumentFlagDisplay from './action-displays/AddDocumentFlagDisplay'
import AddRemoveProjectTagDisplay from './action-displays/AddRemoveProjectTagDisplay'
import AITriggerEventDisplay from './action-displays/AITriggerEventDisplay'
import ApprovalPolicyMatchDisplay from './action-displays/ApprovalPolicyMatchDisplay'
import ClearChipsFromPageDisplay from './action-displays/ClearChipsFromPageDisplay'
import CreateAuditTrailEntryDisplay from './action-displays/CreateAuditTrailEntryDisplay'
import DeleteEmptyTableRowsDisplay from './action-displays/DeleteEmptyTableRowsDisplay'
import EntrataRemoveInvalidTableRowsDisplay from './action-displays/EntrataRemoveInvalidTableRowsDisplay'
import ManageWorkflowAssignmentDisplay from './action-displays/ManageWorkflowAssignmentDisplay'
import MoveCleanDocumentsDisplay from './action-displays/MoveCleanDocumentsDisplay'
import MoveToNextStateDisplay from './action-displays/MoveToNextStateDisplay'
import NoParamsActionsDisplay from './action-displays/NoParamsActionsDisplay'
import ParamDisplay from './action-displays/ParamDisplay'
import QueueEventDisplay from './action-displays/QueueEventDisplay'
import RemoveFlagsOfSpecificTypeDisplay from './action-displays/RemoveFlagsOfSpecificTypeDisplay'
import RunPredictionLinkedModelDisplay from './action-displays/RunPredictionLinkedModelDisplay'
import RunPredictionModelDisplay from './action-displays/RunPredictionModelDisplay'
import SendNotificationDisplay from './action-displays/SendNotificationDisplay'
import SetFieldValueDisplay from './action-displays/SetFieldValueDisplay'
import SetOrganizationByListMatchDisplay from './action-displays/SetOrganizationByListMatchDisplay'
import SplitDocumentDisplay from './action-displays/SplitDocumentDisplay'

type ActionParamsDisplayProps = {
  action: WorkflowStateAction
}

const actionDisplayComponentMap = {
  manage_workflow_assignment: ManageWorkflowAssignmentDisplay,
  create_audit_trail_entry: CreateAuditTrailEntryDisplay,
  move_clean_docs_to_workflow_state: MoveCleanDocumentsDisplay,
  run_prediction_model: RunPredictionModelDisplay,
  add_document_flag: AddDocumentFlagDisplay,
  trigger_event: QueueEventDisplay,
  run_prediction_linked_model: RunPredictionLinkedModelDisplay,
  add_project_tag: AddRemoveProjectTagDisplay,
  remove_project_tags: AddRemoveProjectTagDisplay,
  remove_flags_of_specific_type: RemoveFlagsOfSpecificTypeDisplay,
  set_field_value: SetFieldValueDisplay,
  clear_chips_from_page: ClearChipsFromPageDisplay,
  set_organization_by_list_match: SetOrganizationByListMatchDisplay,
  entrata_delete_bad_line_items: EntrataRemoveInvalidTableRowsDisplay,
  delete_empty_table_rows: DeleteEmptyTableRowsDisplay,
  move_to_next_state: MoveToNextStateDisplay,
  llm_trigger_event: AITriggerEventDisplay,
  split_document: SplitDocumentDisplay,
  apply_approval_policies: ApprovalPolicyMatchDisplay,
  send_notification: SendNotificationDisplay,
}

// Every other display reads a param straight off the action and would throw
// on an empty object.
const emptyParamsSafeActionCodes = new Set(['apply_approval_policies'])

export default function ActionParamsDisplay({
  action,
}: ActionParamsDisplayProps) {
  const isSuperUser = useIsSuperUser()

  const actionCode = action.action_type?.code as
    | keyof typeof actionDisplayComponentMap
    | undefined
  const paramEntries = Object.entries(action.params).toSorted(
    (a, b) =>
      // "?" To account for keys that no longer exist in the schema
      action.action_type?.params_schema.properties[a[0]]?.sort_order -
      action.action_type?.params_schema.properties[b[0]]?.sort_order,
  )

  if (actionCode) {
    // The code is cast from an arbitrary API string, so the lookup can miss.
    const ActionDisplayComponent = actionDisplayComponentMap[actionCode]

    if (
      ActionDisplayComponent &&
      (paramEntries.length > 0 || emptyParamsSafeActionCodes.has(actionCode))
    ) {
      return <ActionDisplayComponent action={action} />
    }
  }

  if (paramEntries.length === 0) {
    return <NoParamsActionsDisplay action={action} />
  }

  return (
    <Stack direction="column" spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {action.action_type?.description}
      </Typography>
      {paramEntries.map(([paramName, paramValue]) => {
        const schemaProperty =
          action.action_type?.params_schema?.properties?.[paramName]
        if (
          !schemaProperty ||
          paramValue === null ||
          schemaProperty.code === 'rule-schema' ||
          (schemaProperty.is_prime && !isSuperUser)
        )
          return null

        return (
          <ParamDisplay
            key={paramName}
            paramSchema={schemaProperty}
            paramValue={paramValue}
          />
        )
      })}
    </Stack>
  )
}
